/* global API */
/**
 * composables -kansiossa olevat tiedostot on tarkoituksella pilkottu päätiedostoista (search.js, services.js etc.)
 * Tämä johtuu siitä, että näissä tiedostoissa olevia datoja tai funktioita käytetään tismalleen samanlaisena eri
 * komponenteissa, jos niihin jostain syystä tarvitsee tehdä muutoksia, ei niitä tarvitse muistaa tehdä useaan paikkaan
 *
 * Vuen mukaan composablejen prefixinä tulisi käyttää use-sanaa (useFunktionNimi)
 *
 * Keywordit
 */

/**
 * Suggested keywords based on search field
 * @param {string} searchString Search string
 * @param {string} selector Index of the data object in the this.data store
 * @returns {(Array|boolean)} Suggested keywords
 */
export function useSuggestedKeywords(searchString, selector) {
	const data = this.data[selector];

	// Tarkistetaan parametrien olemassaolo
	if (searchString && data) {
		if (searchString.length >= 3) {
			const matches = data.filter((item) => item.toLowerCase().includes(searchString.toLowerCase()));

			if (matches) {
				const sorted = matches.sort((a, b) => a.length - b.length || a.localeCompare(b));

				let spliced = sorted.splice(0, 9);

				spliced = spliced.map((m) => {
					const match = m;

					const index = match.toLowerCase().indexOf(searchString.toLowerCase());
					const endIndex = index + searchString.length;

					let output = [match.slice(0, endIndex), '</strong>', match.slice(endIndex)].join('');

					output = [output.slice(0, index), '<strong>', output.slice(index)].join('');

					const value = {
						html: output,
						value: match,
					};

					return value;
				});

				return spliced;
			}
		}
	}

	return false;
}

/**
 * Get suggested publishers based on search string
 * @param {string} searchString Search string
 * @returns {(Array|boolean)} Suggested publishers
 */
export function useSuggestedPublishers(searchString) {
	const data = this.data.publishers;

	// Tarkistetaan parametrien olemassaolo
	if (searchString && data) {
		if (searchString.length >= 3) {
			const matches = data.filter((item) => item.post_title.toLowerCase().includes(searchString.toLowerCase()));

			if (matches) {
				const sorted = matches.sort(
					(a, b) => a.post_title.length - b.post_title.length || a.post_title.localeCompare(b),
				);

				let spliced = sorted.splice(0, 9);

				spliced = spliced.map((m) => {
					const match = m;

					const index = match.post_title.toLowerCase().indexOf(searchString.toLowerCase());
					const endIndex = index + searchString.length;

					let output = [match.post_title.slice(0, endIndex), '</strong>', match.post_title.slice(endIndex)].join('');

					output = [output.slice(0, index), '<strong>', output.slice(index)].join('');

					match.html = output;

					return match;
				});

				return spliced;
			}
		}
	}

	return false;
}

/**
 * Hakusanalistan navigointi nuolinäppäimillä
 * @param {Event} event Event
 * @param {string} selector Selector
 */
export function useNavigateKeywords(event, selector) {
	const ucfirst = selector[0].toUpperCase() + selector.slice(1);
	const showSelector = `show${ucfirst}`;

	if (this.helpers[showSelector]) {
		// tällä hetkellä highlightattu keyword
		const selected = document.querySelector('a.keyword[aria-selected="true"]');

		// asetetaan keywordin index nollaan siltä varalta että valittua keywordia ei ole
		let index = 0;

		// jos keyword on valittu valittu, käytetään sen indexiä
		if (selected) {
			index = parseInt(selected.getAttribute('data-index'), 10);
		}

		if (event.keyCode === 40) {
			// nuoli alas
			event.preventDefault();

			// jos keyword on jo valittu
			if (selected) {
				// nostetaan indexiä yhdellä (siirrytään alemmas listassa)
				index += 1;

				// jos nostettu index on isompi tai yhtä iso kuin keywordien määrä, nollataan index
				// eli toisin sanoen jos listan lopussa painetaan nuoli alas, siirrytään listan alkuun
				if (index >= this.$refs[selector].length) {
					index = 0;
				}

				// asetetaan kaikkien keywordien aria-selected falseksi
				selected.setAttribute('aria-selected', false);

				// asetetaan uuden valitun keywordin aria-select trueksi
				this.$refs[selector][index].setAttribute('aria-selected', true);
			} else if (this.$refs[selector]?.[index]) {
				// jos keywordia ei ollut valittu ennen tätä
				this.$refs[selector][index].setAttribute('aria-selected', true);
			}
		} else if (event.keyCode === 38) {
			// nuoli ylös
			event.preventDefault();

			// jos keyword on jo valittu
			if (selected) {
				// pienennetään indexiä yhdellä (siirrytään ylemmäs listassa)
				index -= 1;

				// jos index on negatiivinen, asetetaan indexiksi hakusanojen määrä - 1 (index alkaa nollasta)
				// toisin sanoen listan alussa siirrytään listan loppuun
				if (index < 0) {
					index = this.$refs[selector].length - 1;
				}

				// asetetaan uuden valitun keywordin aria-select trueksi
				selected.setAttribute('aria-selected', false);

				// asetetaan uuden valitun keywordin aria-select trueksi
				this.$refs[selector][index].setAttribute('aria-selected', true);
			} else if (this.$refs[selector]?.[index]) {
				// jos keywordia ei ollut valittu ennen tätä
				this.$refs[selector][index].setAttribute('aria-selected', true);
			}
		}
	}
}

/**
 * Valitaan hakusana listasta (klikkaamalla hiirellä tai painamalla enteria)
 * @returns {string|boolean} Valittu hakusana tai false jos ei ole valittu
 */
export function useSelectKeyword() {
	// tällä hetkellä highlightattu keyword
	const selected = document.querySelector('a.keyword[aria-selected="true"]');

	if (selected) {
		return selected.getAttribute('data-keyword');
	}

	return false;
}

/**
 * Ehdotettavien hakusanojen hakeminen APIsta
 */
export function useGetKeywords() {
	// haetaan avainsanat
	fetch(`${API.url}/keywords`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	})
		.then((res) => res.json())
		.then((response) => {
			this.data.keywords = response;
		})
		.catch((error) => console.error('Error', error));
}

/**
 * Avainsanan lisääminen
 */
export function useAddKeyword() {
	if (this.helpers.showKeywords) {
		const selected = useSelectKeyword();

		if (selected) {
			this.helpers.keyword = selected;
			this.helpers.showKeywords = false;
		} else {
			this.helpers.showKeywords = false;

			const kw = this.helpers.keyword.toLowerCase();

			if (!this.formData.asiasanat.includes(kw)) {
				this.formData.asiasanat.push(kw);
			}

			this.helpers.keyword = '';
		}
	} else if (this.helpers.keyword.length > 0) {
		const kw = this.helpers.keyword.toLowerCase();

		if (!this.formData.asiasanat.includes(kw)) {
			this.formData.asiasanat.push(kw);
		}

		this.helpers.keyword = '';
	}
}

/**
 * Add keyword to the search field
 */
export function useAddSearchKeyword() {
	if (this.helpers.showKeywords) {
		const selected = useSelectKeyword();

		if (selected) {
			this.helpers.keyword = selected;
			this.helpers.showKeywords = false;
		} else {
			this.helpers.showKeywords = false;

			this.useQuery.meta_query.hakusana = this.helpers.keyword;

			this.search();
		}
	} else {
		this.useQuery.meta_query.hakusana = this.helpers.keyword;

		this.search();
	}
}

/**
 * Does keyword exists in the list
 * @param {string} kwrd Keyword
 * @returns {boolean} True if keyword exists, false if not
 */
export function useKeywordExists(kwrd) {
	const kw = kwrd.toLowerCase();

	return this.data.keywords.some((keyword) => kw === keyword.toLowerCase());
}

/**
 * Poistaa avainsanat arraysta
 * @param {number} index Avainsanan index arrayssa
 */
export function useRemoveKeyword(index) {
	this.formData.asiasanat.splice(index, 1);
}

/**
 * Valitsee keywordin ja piilottaa listan
 * @param {string} keyword Valittu keyword
 */
export function useKeywordClick(keyword) {
	this.helpers.keyword = keyword;
	this.helpers.showKeywords = false;
}
